'use client';

import Link from 'next/link';
import { Heart } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { Img } from '@/shared/components/ui/Img';
import { ImagePlaceholder } from '@/shared/components/ui/ImagePlaceholder';
import { LocationIcon, ArrowRightIcon } from '@/shared/components/icons';
import { Listing } from '../types';
import {
    formatListingPrice,
    formatListingPricePerM2,
    mapPropertyCategoryToText,
    mapPropertySubTypeToText,
    resolveListingArea,
    resolveListingSubtype,
} from '../utils';

const EASE = '[transition-timing-function:cubic-bezier(.22,.61,.36,1)]';

type Props = {
    listing: Listing;
    active?: boolean;
    /** grid = vertikalna karta (2 in a row), row = horizontalna (zoznam) */
    layout?: 'grid' | 'row';
    /** klik otvori detail v mieste (search mode) - href ostava pre middle-click / novy tab */
    onOpen?: (listing: Listing) => void;
};

/**
 * Karta ponuky pre homepage search mode.
 * Vlastny dizajn - nesaha na Lubosov ListingCard (pouzity na /inzeraty).
 */
export function SearchListingCard({ listing, active, layout = 'grid', onOpen }: Props) {
    const subtype = resolveListingSubtype(listing);
    const area = resolveListingArea(listing);
    const rooms = (listing.detail as { roomCount?: number } | null)?.roomCount ?? null;
    const category = mapPropertyCategoryToText(listing.propertyCategory);
    const seller = listing.sellerContact;
    const pricePerM2 = formatListingPricePerM2(listing);
    // slogan vypadol z ListingResponseDto (kontrakt) - fallback na description
    const title = (listing as { slogan?: string }).slogan ?? listing.description ?? '';
    const row = layout === 'row';

    const meta: string[] = [];
    if (rooms) meta.push(rooms === 1 ? '1 izba' : rooms <= 4 ? `${rooms} izby` : `${rooms} izieb`);
    if (area) meta.push(`${area} m²`);
    if (subtype && !rooms) meta.push(mapPropertySubTypeToText(subtype));

    return (
        <Link
            href={`/inzerat/${listing.id}/${listing.slug}`}
            onClick={(e) => {
                if (!onOpen) return;
                // modifikatory / stredne tlacidlo necham prehliadacu (novy tab)
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                onOpen(listing);
            }}
            className={cn(
                'group relative flex h-full overflow-hidden rounded-[18px] bg-white',
                row ? 'flex-row' : 'flex-col',
                'border border-black/[0.04]',
                // maly kludovy tien: kratky ostry + dlhy makky
                'shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_22px_-12px_rgba(0,0,0,0.16)]',
                'transition duration-300',
                EASE,
                'hover:-translate-y-[3px] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_22px_44px_-18px_rgba(0,0,0,0.30)]',
                active &&
                    '-translate-y-[3px] shadow-[0_2px_4px_rgba(0,0,0,0.05),0_22px_44px_-18px_rgba(0,0,0,0.30)] border-[#F3C300]/60',
            )}
        >
            {/* foto */}
            <div
                className={cn(
                    'relative overflow-hidden bg-[#ECEAE4]',
                    row ? 'w-[42%] min-h-[172px] shrink-0' : 'aspect-[4/3]',
                )}
            >
                {listing.photo?.url ? (
                    <Img
                        src={listing.photo.url}
                        alt={title}
                        className={cn(
                            'absolute inset-0 h-full w-full object-cover',
                            'transition duration-700',
                            EASE,
                            'group-hover:scale-[1.045]',
                            active && 'scale-[1.045]',
                        )}
                    />
                ) : (
                    <ImagePlaceholder />
                )}

                <div className="absolute left-[12px] top-[12px] flex gap-[6px]">
                    <span className="rounded-[8px] bg-white/92 px-[9px] py-[5px] text-[11px] font-[500] leading-none text-black backdrop-blur-[6px]">
                        {category}
                    </span>
                </div>

                <button
                    type="button"
                    aria-label="Uložiť ponuku"
                    onClick={(e) => e.preventDefault()}
                    className={cn(
                        'absolute right-[12px] top-[12px] flex size-[32px] items-center justify-center rounded-full',
                        'bg-white/92 text-black backdrop-blur-[6px]',
                        'transition duration-300',
                        EASE,
                        'hover:bg-[#F3C300] hover:scale-[1.08]',
                    )}
                >
                    <Heart className="size-[15px]" strokeWidth={2} />
                </button>
            </div>

            {/* obsah */}
            <div
                className={cn(
                    'flex flex-1 flex-col',
                    row ? 'px-[18px] py-[14px]' : 'px-[18px] pt-[14px] pb-[14px]',
                )}
            >
                <div className="flex items-baseline justify-between gap-[10px]">
                    <p className="text-[19px] font-[600] leading-none tracking-[-0.005em] xl:text-[20px]">
                        {formatListingPrice(listing)}
                    </p>
                    {pricePerM2 && (
                        <p className="shrink-0 text-[11px] leading-none text-black/50">
                            {pricePerM2}
                        </p>
                    )}
                </div>

                <h3 className="mt-[9px] line-clamp-1 text-[14px] font-[500] leading-[1.3] tracking-[0.012em] xl:text-[15px]">
                    {title}
                </h3>

                <div className="mt-[5px] flex items-center gap-[5px] text-[12px] leading-none text-black/55">
                    <LocationIcon className="size-[12px] shrink-0" />
                    <span className="truncate">
                        {listing.street ? `${listing.street}, ` : ''}
                        {listing.municipality?.name ?? ''}
                    </span>
                </div>

                {meta.length > 0 && (
                    <div className="mt-[10px] flex flex-wrap gap-[6px]">
                        {meta.map((m) => (
                            <span
                                key={m}
                                className="rounded-[7px] bg-[#F2F1ED] px-[8px] py-[5px] text-[11px] font-[400] leading-none text-black/75"
                            >
                                {m}
                            </span>
                        ))}
                    </div>
                )}

                {row && listing.description && (
                    <p className="mt-[10px] line-clamp-2 text-[12px] leading-[1.45] text-black/60">
                        {listing.description}
                    </p>
                )}

                {seller && (
                    /* wrapper drzi 30px vzduchu nad linkou; mt-auto tlaci blok na spodok karty.
                       Padding musi byt na wrapperi - na samotnom bloku by 30px pristalo POD
                       linkou (border-t je na jeho hornej hrane), nie nad nou. */
                    <div className="mt-auto pt-[30px]">
                        <div className="flex items-center gap-[9px] border-t border-black/[0.06] pt-[12px]">
                            {seller.photoUrl ? (
                                <Img
                                    src={seller.photoUrl}
                                    alt={seller.name}
                                    className="size-[28px] shrink-0 rounded-full object-cover"
                                />
                            ) : (
                                <span className="size-[28px] shrink-0 rounded-full bg-[#ECEAE4]" />
                            )}
                            <div className="min-w-0 flex-1 leading-none">
                                <p className="truncate text-[12px] font-[500]">{seller.name}</p>
                                {seller.agency?.name && (
                                    <p className="mt-[3px] truncate text-[11px] text-black/50">
                                        {seller.agency.name}
                                    </p>
                                )}
                            </div>
                            <span
                                className={cn(
                                    'flex size-[26px] shrink-0 items-center justify-center rounded-full bg-black text-[#F3C300]',
                                    'opacity-0 translate-x-[6px]',
                                    'transition duration-300',
                                    EASE,
                                    'group-hover:opacity-100 group-hover:translate-x-0',
                                    active && 'opacity-100 translate-x-0',
                                )}
                            >
                                <ArrowRightIcon className="size-[11px]" />
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Link>
    );
}
